(async (algolia) => {
  if (!customElements.get('algolia-predictive-search')) {
    // Wait for AlgoliaSharedUtils to be available
    const waitForSharedUtils = () => {
      return new Promise((resolve) => {
        if (window.AlgoliaSharedUtils) {
          resolve(window.AlgoliaSharedUtils);
        } else {
          setTimeout(() => resolve(waitForSharedUtils()), 50);
        }
      });
    };

    const AlgoliaSharedUtils = await waitForSharedUtils();

    // Wait for InstantSearch to be available from CDN
    const instantsearch = await AlgoliaSharedUtils.waitForInstantSearch();

    class AlgoliaPredictiveSearch extends HTMLElement {
      constructor() {
        super();

        // Prevent multiple instances from making duplicate API calls
        if (window.AlgoliaPredictiveSearchInstance) {
          return;
        }
        window.AlgoliaPredictiveSearchInstance = this;
        // Check if algolia is available
        if (!algolia || !algolia.config) {
          this.showError('Algolia configuration not found');
          return;
        }

        // Create search client directly using algoliasearch
        if (!algolia.externals || !algolia.externals.algoliasearch) {
          this.showError('Algoliasearch not available');
          return;
        }

        this.searchClient = algolia.externals.algoliasearch(
          algolia.config.app_id,
          algolia.config.search_api_key
        );

        // translations
        this.viewAllProductsLabel = this.dataset?.viewAllProductsLabel || 'See all products matching [html]';
        this.productsLabel = this.dataset?.productsLabel || 'Products';
        this.blogLabel = this.dataset?.blogLabel || 'Blog';
        this.pagesLabel = this.dataset?.pagesLabel || 'Pages';
        this.collectionLabel = this.dataset?.collectionLabel || 'Collection';
        this.nothingFoundForTitle = this.dataset?.nothingFoundForTitle || 'Nothing found for [html]';
        this.nothingFoundSubtitle = this.dataset?.nothingFoundSubtitle || 'Try it again with a different term.';
        //config settings
        this.searchTimeout = null;
        this.minSearchLength = parseInt(this.dataset?.minSearchLength) || 1;
        this.searchDelay = parseInt(this.dataset?.searchDelay) || 300;
        this.maxResults = 12; // Limit results for predictive search
        this.totalProductCount = 0; // Store total product count for "See all products" link

        if (!this.searchClient) {
          this.showError('Failed to create search client');
          return;
        }

        this.initializeSearch();
      }

      initializeSearch() {
        // Find all search inputs using multiple selectors (desktop only)
        const searchInputSelectors = [
          '#algoliaAutocompleteInput',
          '#HeaderDesktopSearchInModal',
          '[data-search-input]'
        ];

        const allInputs = [];
        searchInputSelectors.forEach(selector => {
          const inputs = document.querySelectorAll(selector);
          inputs.forEach(input => {
            if (!allInputs.includes(input)) {
              allInputs.push(input);
            }
          });
        });

        if (allInputs.length === 0) {
          return;
        }

        // Store all inputs as a unified search element
        this.searchInputs = allInputs;
        this.currentQuery = '';

        // Create results container if it doesn't exist
        this.createResultsContainer();

        // Add unified event listeners to all inputs
        this.searchInputs.forEach(input => {
          this.addUnifiedEventListeners(input);
        });

        // Add clear button functionality (only once)
        this.addClearButtonHandler();

        // Add global form submission handler
        this.addGlobalFormHandler();
      }

      addUnifiedEventListeners(input) {
        // Input event - treat all inputs as one
        input.addEventListener('input', (e) => {
          const query = e.target.value;
          this.currentQuery = query;

          // Update all other inputs to have the same value
          this.searchInputs.forEach(otherInput => {
            if (otherInput !== input) {
              otherInput.value = query;
            }
          });

          // Also sync with search results page input if it exists
          const searchResultsInput = document.querySelector('.ais-SearchBox-input');
          if (searchResultsInput && searchResultsInput.value !== query) {
            searchResultsInput.value = query;
          }

          this.handleUnifiedSearch(query);
        });

        // Focus event to show results if there's a query
        input.addEventListener('focus', (e) => {
          if (this.currentQuery.length >= this.minSearchLength) {
            this.showResults();
          } else {
            this.hideResults();
          }
        });

        // Handle Enter key press for form submission
        input.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            const form = input.closest('form');
            if (form) {
              // Ensure the current query is set in the input
              if (this.currentQuery) {
                input.value = this.currentQuery;
              }

              // If the input doesn't have name="q", add it temporarily
              if (!input.hasAttribute('name')) {
                input.setAttribute('name', 'q');
              }

              // Store the query in localStorage
              if (input.value.trim()) {
                localStorage.setItem('algolia_search_query', input.value.trim());
              }

              // Let the form submit naturally
            }
          }
        });

        // Blur event to hide results after a delay
        input.addEventListener('blur', () => {
          if (this.currentQuery.length >= this.minSearchLength) {
            setTimeout(() => {
              // Check if the modal is still active before showing results
              const headerSearch = document.querySelector('.header__search');
              if (headerSearch && headerSearch.classList.contains('header__search--is-active')) {
                this.showResults();
              }
            }, 200);
          } else {
            this.hideResults();
          }
        });


        // Form submission - ensure current query is used
        const form = input.closest('form');
        if (form) {
          form.addEventListener('submit', (e) => {
            // Update the form's search input with current query
            const formInput = form.querySelector('input[name="q"]') || input;
            if (formInput && this.currentQuery) {
              formInput.value = this.currentQuery;
            }

            // Ensure the form submits with the correct query
            if (formInput && formInput.value.trim()) {
              // Store the search query in localStorage for consistency
              localStorage.setItem('algolia_search_query', formInput.value.trim());

              // If the input doesn't have name="q", add it temporarily for form submission
              if (!formInput.hasAttribute('name')) {
                formInput.setAttribute('name', 'q');
              }
            }
          });
        }
      }

      addGlobalFormHandler() {
        // Add event listener to document to catch all search form submissions
        document.addEventListener('submit', (e) => {
          const form = e.target;

          if (form && form.matches('[data-search-form]')) {
            // Check if this is a search form
            const searchInput = form.querySelector('input[name="q"]') || form.querySelector('#algoliaAutocompleteInput');

            if (searchInput) {
              // Use current query if available, otherwise use the form input value
              const query = this.currentQuery || searchInput.value.trim();

              if (query) {
                // Update the form input with the current query
                searchInput.value = query;

                // If the input doesn't have name="q", add it temporarily for form submission
                if (!searchInput.hasAttribute('name')) {
                  searchInput.setAttribute('name', 'q');
                }

                // Store the search query in localStorage before navigation
                localStorage.setItem('algolia_search_query', query);

                // Ensure the form action includes the query parameter
                const formAction = form.getAttribute('action');

                if (formAction && formAction.includes('search')) {
                  // The form should submit normally with the query parameter
                  // No need to prevent default - let the form submit naturally
                }
              }
            }
          }
        });
      }

      addClearButtonHandler() {
        const searchModal = document.querySelector('[data-header-search] .search-modal');
        const clearButtons = document.querySelectorAll('[data-search-clear]');
        const searchInput = document.querySelector('[data-searchresults-input]');
        const modalInput = searchModal?.querySelector('[data-search-input]');

        // Add event listener to document to catch all clear button clicks
        clearButtons.forEach(clearButton => clearButton?.addEventListener('click', (e) => {
          // Clear all inputs
          this.searchInputs.forEach(input => input.value = '');
          if (searchInput) {
            searchInput.value = '';
          }

          // Also clear search results page input if it exists
          const searchResultsInput = document.querySelector('.ais-SearchBox-input');
          if (searchResultsInput) {
            searchResultsInput.value = '';
          }

          this.currentQuery = '';
          this.hideResults();

          // Focus on modal input or first search input
          (modalInput || this.searchInputs[0])?.focus();
        }));
      }

      createResultsContainer() {
        // Check if results container already exists globally
        let resultsContainer = document.querySelector('.algolia-predictive-search-results');

        if (!resultsContainer) {
          resultsContainer = document.createElement('div');
          resultsContainer.className = 'algolia-predictive-search-results';
          resultsContainer.style.display = 'none';

          // Find the search modal container and add results after the search bar
          const searchModal = document.querySelector('.search-modal');
          const searchBar = searchModal?.querySelector('.search-modal__bar');
          const form = this.querySelector('form');

          // Insert after search bar or form
          const insertAfter = searchBar || form;
          insertAfter?.parentNode?.insertBefore(resultsContainer, insertAfter.nextSibling);
        }
        this.resultsContainer = resultsContainer;
      }

      handleUnifiedSearch(query) {
        // Clear previous timeout
        this.searchTimeout && clearTimeout(this.searchTimeout);

        // Show/hide clear button
        const clearButton = this.querySelector('[data-search-clear]');
        clearButton?.classList.toggle('hidden', query.length === 0);

        // Hide results if query is too short
        if (query.length < this.minSearchLength) {
          this.hideResults();
          return;
        }

        // Debounce search
        this.searchTimeout = setTimeout(() => this.performSearch(query), this.searchDelay);
      }

      async performSearch(query) {
        try {
          if (!this.searchClient) {
            this.showError('Search client not available');
            return;
          }

          // Don't search if query is too short
          if (query.length < this.minSearchLength) {
            this.hideResults();
            return;
          }

          // Show loading state
          this.showLoading();

          // Build search requests array
          const searchRequests = [];

          // Search products (limited for predictive search)
          if (algolia.config.index_products) {
            searchRequests.push({
              indexName: `${algolia.config.index_prefix}products`,
              query: query,
              hitsPerPage: this.maxResults,
              distinct: true,
              attributesToRetrieve: ['*'],
              attributesToHighlight: ['title', 'description'],
              highlightPreTag: '<mark>',
              highlightPostTag: '</mark>'
            });
          }

          // Search collections
          if (algolia.config.index_collections) {
            searchRequests.push({
              indexName: `${algolia.config.index_prefix}collections`,
              query: query,
              hitsPerPage: 8,
              distinct: true,
              attributesToRetrieve: ['*'],
              attributesToHighlight: ['title', 'description'],
              highlightPreTag: '<mark>',
              highlightPostTag: '</mark>'
            });
          }

          // Search articles
          if (algolia.config.index_articles) {
            searchRequests.push({
              indexName: `${algolia.config.index_prefix}articles`,
              query: query,
              hitsPerPage: 4,
              distinct: true,
              attributesToRetrieve: ['*'],
              attributesToHighlight: ['title', 'excerpt'],
              highlightPreTag: '<mark>',
              highlightPostTag: '</mark>'
            });
          }

          // Search pages
          if (algolia.config.index_pages) {
            searchRequests.push({
              indexName: `${algolia.config.index_prefix}pages`,
              query: query,
              hitsPerPage: 2,
              distinct: true,
              attributesToRetrieve: ['*'],
              attributesToHighlight: ['title', 'content'],
              highlightPreTag: '<mark>',
              highlightPostTag: '</mark>'
            });
          }

          const results = await this.searchClient.search(searchRequests);

          // Process results - results.results is an array of search results
          const processedResults = {
            products: results.results?.[0]?.hits || [],
            collections: results.results?.[1]?.hits || [],
            articles: results.results?.[2]?.hits || [],
            pages: results.results?.[3]?.hits || []
          };

          // Get total product count from the first products search
          this.totalProductCount = results.results?.[0]?.nbHits || 0;
          this.displayResults(processedResults, query);

        } catch (error) {
          this.showError('Search failed. Please try again.');
        }
      }

      displayResults(results, query) {
        const { products, collections, articles, pages } = results;

        // Check if we have any results
        const hasResults = products.length > 0 || collections.length > 0 || articles.length > 0 || pages.length > 0;

        if (!hasResults) {
          this.showNoResults(query);
          return;
        }

        // Build results HTML with proper layout
        let resultsHTML = '<div class="algolia-predictive-search-results__inner aa-columns-container aa-with-articles aa-with-collections aa-with-footer aa-without-powered-by aa-with-products aa-with-pages">';


        // Left sidebar for links (collections, articles, pages)
        const leftColumnResults = [...collections, ...articles, ...pages];
        if (leftColumnResults.length > 0) {
          resultsHTML += '<div class="algolia-sidebar">';

          // Collections
          if (collections.length > 0) {
            resultsHTML += this.renderCollectionResults(collections);
          }

          // Articles
          if (articles.length > 0) {
            resultsHTML += this.renderArticleResults(articles);
          }

          // Pages
          if (pages.length > 0) {
            resultsHTML += this.renderPageResults(pages);
          }

          resultsHTML += '</div>';
        }

        // Right section for products (always show if we have products)
        if (products.length > 0) {
          resultsHTML += '<div class="algolia-products">';
          resultsHTML += this.renderProductResults(products, query);
          resultsHTML += '</div>';
        }

        resultsHTML += '</div>';

        this.resultsContainer.innerHTML = resultsHTML;

        // Add click event listener to "See all products" link
        const seeAllLink = this.resultsContainer.querySelector('.algolia-products-footer a');
        if (seeAllLink) {
          seeAllLink.addEventListener('click', (e) => {
            e.preventDefault();
            const currentQuery = this.currentQuery || query;
            const searchUrl = `/search?q=${encodeURIComponent(currentQuery)}&type=product`;
            window.location.href = searchUrl;
          });
        }

        this.showResults();
      }

      renderCollectionResults(collections) {
        let html = '<div class="algolia-section">';
        html += `<p class="algolia-section-title">${this.collectionLabel}</p>`;
        html += '<ul class="algolia-list">';

        collections.forEach(collection => {
          const url = collection.url || `/collections/${collection.handle}`;
          html += `<li>`;
          html += `<a href="${url}" title="${collection.title}">`;
          html += this.highlightText(collection.title, collection._highlightResult?.title?.value);
          html += '</a></li>';
        });

        html += '</ul></div>';
        return html;
      }

      renderPageResults(pages) {
        let html = '<div class="algolia-section">';
        html += `<p class="algolia-section-title">${this.pagesLabel}</p>`;
        html += '<ul class="algolia-list">';

        pages.forEach(page => {
          const url = page.url || `/${page.handle}`;
          html += `<li>`;
          html += `<a href="${url}" title="${page.title}">`;
          html += this.highlightText(page.title, page._highlightResult?.title?.value);
          html += '</a></li>';
        });

        html += '</ul></div>';
        return html;
      }

      renderArticleResults(articles) {
        let html = '<div class="algolia-section">';
        html += `<p class="algolia-section-title">${this.blogLabel}</p>`;
        html += '<ul class="algolia-list">';

        articles.forEach(article => {
          const url = article.url || `/blogs/${article.blog_handle}/${article.handle}`;
          html += `<li>`;
          html += `<a href="${url}" title="${article.title}">`;
          html += this.highlightText(article.title, article._highlightResult?.title?.value);
          html += '</a></li>';
        });

        html += '</ul></div>';
        return html;
      }

      renderProductResults(products, query) {
        let html = '<div class="algolia-products-section">';
        html += `<p class="algolia-section-title">${this.productsLabel}</p>`;

        // Create two columns for products
        html += '<div class="algolia-products-grid">';

        products.forEach(product => {
          const url = product.url || `/products/${product.handle}`;
          const image = product.image || product.featured_image || '/assets/no-image.gif';
          // Get raw prices for comparison
          const rawPrice = product.price;
          const rawCompareAtPrice = product.compare_at_price;

          // Determine if product is on sale using raw prices
          const isOnSale = rawCompareAtPrice && rawCompareAtPrice > 0 && rawCompareAtPrice > rawPrice;

          // Format prices for display
          const price = AlgoliaSharedUtils.formatPrice(rawPrice, {}, this);
          const compareAtPrice = isOnSale ? AlgoliaSharedUtils.formatPrice(rawCompareAtPrice, {}, this) : null;

          html += `<div class="algolia-product-item">`;
          html += `<a href="${url}" title="${product.title}">`;

          // Product image (circular)
          html += '<div class="algolia-product-image">';
          // Generate optimized image URL with fixed 54px width (including 2x for high-res screens)
          if (image && !image.includes('/assets/')) {
            const baseImageUrl = image.split('?')[0];
            const optimizedImage = `${baseImageUrl}?width=55&height=68&crop=center`;
            const optimizedImage2x = `${baseImageUrl}?width=110&height=138&crop=center`;
            html += `<img src="${optimizedImage}" srcset="${optimizedImage} 1x, ${optimizedImage2x} 2x" alt="${product.title}" loading="eager">`;
          } else {
            html += `<img src="${image}" alt="${product.title}" loading="eager">`;
          }
          html += '</div>';

          // Product info
          html += '<div class="algolia-product-info">';
          html += `<div class="algolia-product-title">${this.highlightText(product.title, product._highlightResult?.title?.value)}</div>`;

          // Price
          html += '<div class="algolia-product-price">';
          if (isOnSale) {
            html += `<span class="algolia-product-price-compare">${compareAtPrice}</span>`;
            html += `<span class="algolia-product-price-sale">${price}</span>`;
          } else {
            html += `<span class="algolia-product-price-regular">${price}</span>`;
          }
          html += '</div>';

          html += '</div>';
          html += '</a>';
          html += '</div>';
        });

        html += '</div>';

        // Footer with "See all products" link
        html += '<div class="algolia-products-footer">';
        html += `<a href="/search?q=${encodeURIComponent(this.currentQuery || query)}&type=product">`;
        html += `${this.viewAllProductsLabel.replace(/\[html\]|{{ html }}/g, `'${this.currentQuery || query}'`)} (${this.totalProductCount})`;
        html += '</a>';
        html += '</div>';

        html += '</div>';

        return html;
      }

      highlightText(text, highlightedText) {
        if (highlightedText) {
          return highlightedText;
        }
        return text;
      }


      showResults() {
        this.resultsContainer && (this.resultsContainer.style.display = 'block');

        // Add active class to header search
        const headerSearch = document.querySelector('.header__search');
        headerSearch?.classList.contains('header__search--is-active') ||
          headerSearch?.classList.add('header__search--is-active');
      }

      hideResults() {
        if (this.resultsContainer) {
          this.resultsContainer.style.display = 'none';
          this.resultsContainer.innerHTML = ''; // Clear the search results content
        }
      }

      showLoading() {
        this.resultsContainer.innerHTML = `
          <div class="algolia-predictive-search-results__inner">
            <div class="is-col-12 center">
              <p>Searching...</p>
            </div>
          </div>
        `;
        this.showResults();
      }

      showNoResults(query) {
        this.resultsContainer.innerHTML = `
          <div class="algolia-predictive-search-results__inner">
            <div class="is-col-12 center">
              <p>${this.nothingFoundForTitle.replace(/\[html\]|{{ html }}/g, `${query}`)}</p>

              <p>${this.nothingFoundSubtitle}</p>
            </div>
          </div>
        `;
        this.showResults();
      }

      showError(message) {
        this.resultsContainer.innerHTML = `
          <div class="algolia-predictive-search-results__inner">
            <div class="is-col-12 center">
              <p>${message}</p>
            </div>
          </div>
        `;
        this.showResults();
      }
    }

    window.AlgoliaPredictiveSearch = AlgoliaPredictiveSearch;
    customElements.define('algolia-predictive-search', AlgoliaPredictiveSearch);
  }
})(window.algoliaShopify);
